// Staff sign-in with Amazon Cognito (SRP: the password never leaves the browser in plain form).
// Handles first sign-in (new password + authenticator app setup) and normal MFA sign-in.
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';

let pool;
export async function initAuth() {
  const cfg = await (await fetch('/config.json', { cache: 'no-store' })).json();
  pool = new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.userPoolClientId });
  return cfg;
}

// Returns { token, email, groups } for a signed-in user (refreshing the token if needed), or null.
export function currentSession() {
  return new Promise((resolve) => {
    const user = pool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err, session) => {
      if (err || !session?.isValid()) return resolve(null);
      const id = session.getIdToken();
      const p = id.decodePayload();
      resolve({ token: id.getJwtToken(), email: p.email, groups: p['cognito:groups'] || [] });
    });
  });
}

export async function idToken() {
  const s = await currentSession();
  if (!s) throw Object.assign(new Error('Your session expired. Please sign in again.'), { status: 401 });
  return s.token;
}

export function signOut() {
  pool.getCurrentUser()?.signOut();
}

// `ask(step, data)` shows a prompt and resolves with what the person typed.
export function signIn(email, password, ask) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool });
    const step = (fn) => async (...args) => { try { await fn(...args); } catch (e) { reject(e); } };
    const cb = {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(new Error(friendly(err))),
      newPasswordRequired: step(async () => {
        const pw = await ask('newPassword');
        user.completeNewPasswordChallenge(pw, {}, cb);
      }),
      mfaSetup: step(async () => user.associateSoftwareToken(cb)),
      associateSecretCode: step(async (secret) => {
        const code = await ask('setupTotp', { secret, email });
        user.verifySoftwareToken(code, 'Authenticator app', cb);
      }),
      totpRequired: step(async () => {
        const code = await ask('totp');
        user.sendMFACode(code, cb, 'SOFTWARE_TOKEN_MFA');
      }),
    };
    user.authenticateUser(new AuthenticationDetails({ Username: email.trim().toLowerCase(), Password: password }), cb);
  });
}

function friendly(err) {
  const m = err?.message || '';
  if (/Incorrect username or password|User does not exist/i.test(m)) return 'That email and password don\'t match.';
  if (/Invalid code|Code mismatch|EnableSoftwareTokenMFA/i.test(m)) return 'That code didn\'t work. Codes change every 30 seconds; please try again.';
  if (/Password does not conform|policy/i.test(m)) return 'Passwords need at least 12 characters, with upper and lower case letters, a number, and a symbol.';
  if (/attempts exceeded/i.test(m)) return 'Too many attempts. Please wait a few minutes and try again.';
  return m || 'Sign-in failed. Please try again.';
}
